import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import type { JobDetails } from '@xing-com/crate-jobs-helpers';
import { useHost } from '@xing-com/crate-xinglet';

import DefaultImage from './assets/og-xing-logo.png';

type JobMetadata = {
  job: JobDetails;
};

export const Metadata: React.FC<JobMetadata> = ({ job }) => {
  const { formatMessage, formatDate } = useIntl();
  const { isLoggedOut } = useLoginState();
  const { getHostname } = useHost();

  const {
    activatedAt,
    careerLevel,
    company,
    facts: { employmentType },
    industry,
    keywords,
    locations,
    title: jobTitle,
    url,
  } = job;

  const cities =
    locations
      .map((location) => location.city)
      .filter(Boolean)
      .join(', ') ?? '';

  const title = `${formatMessage(
    {
      id: isLoggedOut
        ? 'JOBS_JOB_DETAILS_SEO_TITLE_TAG_LOGGED_OUT'
        : 'JOBS_JOB_DETAILS_SEO_TITLE_TAG_LOGGED_IN',
    },
    { job_title: jobTitle, work_location: cities }
  )} | ${formatMessage({ id: 'JOBS_JOB_DETAILS_TITLEBAR_SUFFIX' })}`;

  const metaTitle = `${jobTitle} | ${formatMessage({
    id: 'JOBS_JOB_DETAILS_TITLEBAR_SUFFIX',
  })}`;

  const metaDescription = formatMessage(
    { id: 'JOBS_JOB_DETAILS_SEO_META_DESCRIPTION_TAG_POSTING_DETAIL' },
    {
      function: jobTitle,
      company_name: company?.name,
      city: cities,
      industry: industry?.name,
      job_type: employmentType?.name,
      level: careerLevel?.name,
      date: formatDate(activatedAt ?? '', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
    }
  );

  const defaultPageTitle = formatMessage({
    id: 'JOBS_JOB_DETAILS_SE_DEFAULT_PAGETITLE',
  });
  const defaultPageTitleSuffix = formatMessage({
    id: 'JOBS_JOB_DETAILS_SE_DEFAULT_PAGETITLE_SUFFIX',
  });
  const defaultTitle = `${defaultPageTitle} | ${defaultPageTitleSuffix}`;
  const defaultOgTitle = title ?? defaultPageTitle;

  return (
    <Helmet>
      <title>{title ?? defaultTitle}</title>
      {metaDescription && <meta name="description" content={metaDescription} />}
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.toString()} />
      )}
      <meta property="og:title" content={metaTitle ?? defaultOgTitle} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="XING" />
      <meta
        property="og:image"
        content={company?.logo ?? `${getHostname()}${DefaultImage}`}
      />
      {url && <meta property="og:url" content={url} />}
      {metaDescription && (
        <meta property="og:description" content={metaDescription} />
      )}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:site" content="@XING_de" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={url} />
    </Helmet>
  );
};
